import React from 'react';

import {
  ProductTile,
  ProductTileContent,
  ProductDetails,
  ProductTileInfoButton,
  ProductTitle,
  ProductImage,
  ProductSubtitle,
  ProductTileCtaSection,
  ProductTileFooter
} from '@shared-components/molecules/ProductTile';
import Button from '@shared-components/atoms/Button';
import Badge from '@shared-components/atoms/Badge';
import { FormControl, FormHelperText, InputLabel, Select, SelectChangeEvent } from '@mui/material';
import { KeyboardArrowDownOutlined } from '@mui/icons-material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import useSelectStyles from '@/common/components/LocalizedProductTile/styles';
import { RecipeTileData } from './MultiRecipeSelectionTile';
import useShowNewBadge from '@/menu/hooks/useShowNewBadge';
import formatPrice from '@/common/formattedPrice';
import css from './MultiRecipeSelection.module.css';

const useStyles = makeStyles((theme) => createStyles({
  productContainer: {
    alignItems: 'stretch',
    marginBottom: '16px'
  },
  modifierDropdownSelectComponent: {
    [theme.breakpoints.up('md')]: {
      width: '50%'
    },
    '& [aria-invalid="true"]': {
      backgroundColor: "#FAEAE9",
      borderColor: '#E21216'
    }
  },
  sizeText: {
    marginTop: '8px',
    marginBottom: '4px'
  }
}));

type MultiRecipeSelectionTileWithSizeProps = {
  isLoading?: boolean;
  product: RecipeTileData;
  stepName?: string;
};

function MultiRecipeSelectionTileWithSizeOption({
  product, stepName, isLoading = false
}: MultiRecipeSelectionTileWithSizeProps) {
  const {
    id,
    isCyo,
    name,
    description,
    outOfStock,
    sizeCrustSelection,
    imageURL,
    formattedPrice,
    nutritionInfo,
    onCustomize,
    onAddToDeal,
    showSizeAndCrustSelection,
    primaryCtaButtonText,
    shouldShowPrice,
    shouldShowActionRow,
    sizeOptions
  } = product;

  const { shouldShowNewBadge } = useShowNewBadge(id);
  const title = isCyo ? `${name} ${stepName ?? ''}` : name;
  const testId = isCyo ? 'cyo-pizza-tile' : 'upcharge-recipe-tile';
  const selectClasses = useSelectStyles({});
  const classes = useStyles({});
  const [selectedSize, setSelectedSize] = React.useState<string>();
  const [error, setErrorMsg] = React.useState<string | null>(null);

  function handleSizeChange(event: SelectChangeEvent<string>) {
    setErrorMsg('');
    setSelectedSize(event.target.value);
  }

  function handleAddToDealButtonClick() {
    if (selectedSize) {
      setErrorMsg('');
      onAddToDeal(1, selectedSize);
    } else {
      setErrorMsg('Choose your size');
    }
  }

  return (
    <ProductTile testId={testId}>
      <ProductTileContent>
        <ProductImage imageURL={imageURL} imageAltText={name} productName={name} />
        <ProductDetails className={classes.productContainer}>
          <div className={css['product-tile__product-name-crust-container']} data-foo="richard">
            <ProductTitle>{title}</ProductTitle>
            {!outOfStock && (
              <ProductTileInfoButton productName={title} productDescription={description} />
            )}
          </div>
          {outOfStock || shouldShowNewBadge && (
            <div className={css['product-tile__badges-container']}>
              {outOfStock && (<Badge variant="disabled">Sold out</Badge>)}
              {shouldShowNewBadge && <Badge variant="primary">New</Badge>}
            </div>
          )}
          {showSizeAndCrustSelection && <ProductSubtitle className={css['product-tile__product-size-crust']}>{sizeCrustSelection}</ProductSubtitle>}
          {shouldShowPrice && <ProductSubtitle>Starting at {formattedPrice}</ProductSubtitle>}
          {sizeOptions && (
            <>
              <InputLabel className={clsx(selectClasses.modifierDropdownLabel, classes.sizeText)} htmlFor={`size-select-${id}`}>
                Size
              </InputLabel>
              <FormControl error={!!error}>
                <Select
                  className={clsx(selectClasses.modifierDropdownSelectComponent, classes.modifierDropdownSelectComponent)}
                  IconComponent={KeyboardArrowDownOutlined}
                  inputProps={{
                    className: clsx(selectClasses.modifierDropdownSelectElement ),
                    id: `size-select-${id}`
                  }}
                  data-testid='size-selector'
                  native
                  onChange={handleSizeChange}
                  value={selectedSize}
                >
                  {!selectedSize && <option value="">Choose your size</option>}
                  {sizeOptions.map(({ value, name: optionName, upcharge }) => {
                    const hasUpcharge = upcharge != null && upcharge > 0;
                    const displayUpcharge = hasUpcharge ? ` (+${formatPrice(upcharge)})` : '';
                    return (
                      <option
                        key={value}
                        value={value}
                      >
                        {optionName}{displayUpcharge}
                      </option>
                    );
                  })}
                </Select>
                <FormHelperText>{error}</FormHelperText>
              </FormControl>
            </>
          )}
        </ProductDetails>
        {shouldShowActionRow && (
          <ProductTileCtaSection>
            <Button
              aria-label={`Customize ${name}`}
              className={css['product-tile__cta-link']}
              disabled={outOfStock}
              onClick={onCustomize}
              variant="link"
            >
              Customize
            </Button>
            <Button
              aria-label={`${primaryCtaButtonText} ${name}`}
              className={css['product-tile__add-to-deal-button']}
              disabled={outOfStock}
              isLoading={isLoading}
              onClick={handleAddToDealButtonClick}
              variant="secondary"
            >
              {primaryCtaButtonText}
            </Button>
          </ProductTileCtaSection>
        )}
      </ProductTileContent>
      {nutritionInfo && (<ProductTileFooter>{nutritionInfo}</ProductTileFooter>)}
    </ProductTile>
  );
}

export default React.memo(MultiRecipeSelectionTileWithSizeOption);
